import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import { useTranslations } from '@npco/common-utils-translations'
import {
  ButtonFill,
  ButtonLink,
  Flex,
  Tutorial,
} from '@npco/zeller-design-system'

import NotificationsDesktop from 'assets/images/notifications/notifications-desktop.jpg'
import NotificationsMobile from 'assets/images/notifications/notifications-mobile.jpg'
import NotificationsTablet from 'assets/images/notifications/notifications-tablet.jpg'
import { useImagePreloader } from 'hooks/useImagePreloader/useImagePreloader'
import { ROUTE } from 'const/routes'

import { MainContent } from '../common/components/MainContent/MainContent'
import { ImageContainer } from '../SetupFlow/components/ImageContainer/ImageContainer'
import * as styled from '../StartupModals.styled'
import { useNotificationsModal } from './hooks/useNotificationsModal'
import { translations } from './NotificationsModal.i18n'

export const NotficationsModalDeps = {
  useImagePreloader,
  useNavigate,
  useNotificationsModal,
  useTranslations,
}

export const NotificationsModal = () => {
  const {
    useImagePreloader,
    useNavigate,
    useNotificationsModal,
    useTranslations,
  } = NotficationsModalDeps

  const t = useTranslations(translations)

  const navigate = useNavigate()

  const { shouldShowNotificationsModal, disableNotificationsModal } =
    useNotificationsModal()

  const [isOpen, setIsOpen] = useState(shouldShowNotificationsModal)

  const { imagesPreloaded } = useImagePreloader([
    NotificationsMobile,
    NotificationsTablet,
    NotificationsDesktop,
  ])

  useEffect(() => {
    disableNotificationsModal()
    // componentDidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeModal = useCallback(async () => setIsOpen(false), [])

  const bulletPoints = useMemo(
    () => [t('point1'), t('point2'), t('point3'), t('point4')],
    [t]
  )

  const onPrimaryButtonClick = useCallback(async () => {
    navigate(ROUTE.PORTAL_NOTIFICATIONS)
  }, [navigate])

  if (!imagesPreloaded) {
    return null
  }

  return (
    <styled.CustomizedModal
      className="animated-content"
      closeTimeoutMS={350}
      isOpen={isOpen}
      onCancel={closeModal}
      overlayClassName="animated-overlay"
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <Tutorial.TutorialLayout
        Footer={
          <Flex
            justifyContent="space-between"
            padding={{
              _: '12px 24px 24px',
              SM: '12px 32px 32px',
              LG: '12px 0 0 0',
            }}
            width="100%"
          >
            <ButtonLink onClick={closeModal}>
              {t('secondaryButtonLabel')}
            </ButtonLink>
            <ButtonFill onClick={onPrimaryButtonClick}>
              {t('primaryButtonLabel')}
            </ButtonFill>
          </Flex>
        }
        Illustration={
          <ImageContainer
            mobileImagePath={NotificationsMobile}
            tabletImagePath={NotificationsTablet}
            desktopImagePath={NotificationsDesktop}
          />
        }
        MainContent={
          <MainContent
            bulletPoints={bulletPoints}
            description={t('description')}
            title={t('title')}
            testId="notifications-modal-content"
          />
        }
      />
    </styled.CustomizedModal>
  )
}
